import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8070";
const API_V1_URL = `${API_BASE_URL}/api/v1`;

let refreshPromise = null;

const IMPERSONATION_STORAGE_KEYS = {
  accessToken: "impersonation.originalAccessToken",
  refreshToken: "impersonation.originalRefreshToken",
  user: "impersonation.originalUser"
};

const readStoredUser = (key) => {
  const value = localStorage.getItem(key);

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    localStorage.removeItem(key);
    return null;
  }
};

export const normalizeApiError = (error) => ({
  success: false,
  status: error?.response?.status || 0,
  code:
    error?.response?.data?.error?.code ||
    (typeof error?.response?.data?.error === "string" ? error.response.data.error : null) ||
    error?.code ||
    null,
  message:
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    "Something went wrong",
  details: error?.response?.data?.error?.details || error?.response?.data?.details || null,
  originalError: error
});

export const tokenStorage = {
  getAccessToken() {
    return localStorage.getItem("accessToken") || localStorage.getItem("token") || "";
  },
  getRefreshToken() {
    return localStorage.getItem("refreshToken") || "";
  },
  getSessionUser() {
    return readStoredUser("loginData") || readStoredUser("user");
  },
  setSession({ accessToken, refreshToken, user }) {
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("token", accessToken);
    }

    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    } else if (refreshToken === null) {
      localStorage.removeItem("refreshToken");
    }

    if (user) {
      localStorage.setItem("loginData", JSON.stringify(user));
      localStorage.setItem("user", JSON.stringify(user));
    }
  },
  saveImpersonationOrigin({ accessToken, refreshToken, user }) {
    if (accessToken) {
      localStorage.setItem(IMPERSONATION_STORAGE_KEYS.accessToken, accessToken);
    }

    if (refreshToken) {
      localStorage.setItem(IMPERSONATION_STORAGE_KEYS.refreshToken, refreshToken);
    } else {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEYS.refreshToken);
    }

    if (user) {
      localStorage.setItem(IMPERSONATION_STORAGE_KEYS.user, JSON.stringify(user));
    }
  },
  getImpersonationOrigin() {
    const accessToken = localStorage.getItem(IMPERSONATION_STORAGE_KEYS.accessToken) || "";
    const refreshToken = localStorage.getItem(IMPERSONATION_STORAGE_KEYS.refreshToken) || "";
    const user = readStoredUser(IMPERSONATION_STORAGE_KEYS.user);

    if (!accessToken || !user) {
      return null;
    }

    return {
      accessToken,
      refreshToken,
      user
    };
  },
  hasImpersonationOrigin() {
    return Boolean(this.getImpersonationOrigin());
  },
  clearImpersonationOrigin() {
    Object.values(IMPERSONATION_STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  },
  clearSession() {
    [
      "accessToken",
      "token",
      "refreshToken",
      "user",
      "loginData",
      "profile_type",
      "profile_id",
      "hospital_id",
      "management_id",
      "doctor_id",
      "ownerId",
      "petOwnerId",
      "pet_owner_id"
    ].forEach((key) => localStorage.removeItem(key));

    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("vetcastle:session-cleared"));
    }
  }
};

const apiClient = axios.create({
  baseURL: API_V1_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json"
  }
});

apiClient.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

const refreshAccessToken = async () => {
  const refreshToken = tokenStorage.getRefreshToken();

  if (!refreshToken) {
    throw new Error("Session expired");
  }

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_V1_URL}/auth/refresh`, { refreshToken })
      .then((response) => {
        const data = response.data?.data || response.data || {};
        const accessToken = data.accessToken || data.access_token;
        const nextRefreshToken = data.refreshToken || data.refresh_token || refreshToken;

        if (!accessToken) {
          throw new Error("Refresh response did not include an access token");
        }

        tokenStorage.setSession({
          accessToken,
          refreshToken: nextRefreshToken,
          user: data.user
        });

        return accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 403 &&
      (error.response?.data?.error === "LIMIT_REACHED" || error.response?.data?.error?.code === "LIMIT_REACHED")
    ) {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("vetcastle:limit-reached", {
            detail: {
              message: error.response?.data?.message || "Upgrade your plan to continue."
            }
          })
        );
      }
    }

    if (error.response?.status !== 401 || originalRequest?._retry) {
      return Promise.reject(normalizeApiError(error));
    }

    originalRequest._retry = true;

    try {
      const token = await refreshAccessToken();
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      tokenStorage.clearSession();
      return Promise.reject(normalizeApiError(refreshError));
    }
  }
);

export { API_BASE_URL, API_V1_URL, apiClient };
